import { h, Component } from 'preact';
import Store from 'store/store';
import * as actions from 'store/actions';
import pure from 'utils/pure';
import { oneTrust } from 'utils/gdpr';

let levelPixels = {};

function loadLevelPixels(level) {
	let content = {};
	try {
		// content = require(baseDir) || {};
		// Have to do that because doesn't work if not a string litteral in the require
		switch (level) {
			case 'app': content = require('<%TITLE_REGION_SRC_DIR%>/components/shared/pixels/pixels'); break;
			case 'title': content = require('<%TITLE_SRC_DIR%>/components/shared/pixels/pixels'); break;
			case 'studioRegion': content = require('<%STUDIO_REGION_SRC_DIR%>/components/shared/pixels/pixels'); break;
			case 'studio': content = require('<%STUDIO_SRC_DIR%>/components/shared/pixels/pixels'); break;
			case 'region': content = require('<%REGION_DIR%>/components/shared/pixels/pixels'); break;
			case 'base': content = require('<%SRC_DIR%>/components/shared/pixels/pixels'); break;
		}
		content = content || {};
		content.level = level;
	} catch (err) {
		// In case it's an actual error within the pixel file don't ignore it
		if (err && err.code !== 'MODULE_NOT_FOUND') {
			throw err;
		}
		// console.error('No ' + level + ' pixel');
	}
	return levelPixels[level] = content;
}

loadLevelPixels('app');
loadLevelPixels('title');
loadLevelPixels('studioRegion');
loadLevelPixels('studio');
loadLevelPixels('region');
loadLevelPixels('base');

// Lets see which ones we need to ignore
const disabledLevels = Object.assign(
	{},
	levelPixels.base.disabledLevels,
	levelPixels.region.disabledLevels,
	levelPixels.studio.disabledLevels,
	levelPixels.studioRegion.disabledLevels,
	levelPixels.title.disabledLevels,
	levelPixels.app.disabledLevels
);

// Could use Object.assign.apply({}, [{}, ...Object.values(levelPixels).reverse().map(pix => pix.disabledLevels)])

const pixels = Object.keys(levelPixels).filter(lvl => {
	let pxl = levelPixels[lvl];

	// check if the file exists and if it's not disabled
	return !!pxl.level && !disabledLevels[pxl.level];
}).map(pxl => levelPixels[pxl]);

// Add the listeners to the store
pixels.forEach(pixel => {
	if (!pixel.storeListeners) {
		// There are no actions detected
		return;
	}

	pixel.queue = [];

	const listenToAction = action => fn => {
		if (typeof fn === 'object' && fn instanceof oneTrust.Pixel) {
			fn = fn.createTrigger();
		}
		Store.on(action, function () {
			const args = Array.prototype.slice.call(arguments, 0);
			const pixelConsent = Store.get().client.pixelConsent;
			if (pixelConsent !== false) {
				fn.apply(null, args);
			} else {
				pixel.queue.push({ fn, ctx: null, args });
			}
		});
	};

	// Loop through all the actions and add the listener
	Object.keys(pixel.storeListeners).forEach(action => {
		let effect = pixel.storeListeners[action];

		const listener = listenToAction(action);

		if (Array.isArray(effect)) {
			const effects = effect;
			return effects.forEach(listener);
		}

		return listener(effect);
	});
});

Store.on(actions.ENABLE_PIXELS, () => {
	pixels.forEach(pixel => {
		const { queue } = pixel;
		queue.forEach(({ fn, ctx, args }) => {
			fn.apply(ctx, args);
		});
		queue.length = 0;
	});
});

function renderPixel(comp, state) {
	if (!comp) {
		// No render function found
		return;
	}

	// Render out the component
	let rendered = h(comp, {
		appData: state.store.appData,
		activePage: state.store.activePage
	});

	// Try removing noscript tags
	if (typeof window !== 'undefined') {
		let nodeName = rendered && rendered.nodeName;
		let functionalComp = nodeName && typeof nodeName === 'function' && !(nodeName.prototype && nodeName.prototype.render);
		if (functionalComp) {
			rendered = nodeName(rendered.attributes);
		}
		if (rendered && rendered.children) {
			rendered.children = rendered.children.filter(
				node => node && node.nodeName !== 'noscript'
			);
		}
	}
	return rendered;
}

class PixelRenderer extends Component {
	constructor(props) {
		super(props);
		// Lets get the store at the start, so the component doesn't rerender
		// everytime the store does
		this.state.store = Store.get();
	}

	getPixelComponent() {}

	render(props, state) {
		// Render out all the pixels that have a render function
		// Reverse the array so that the base gets rendered first
		let list = pixels.map(pixel => pixel && renderPixel(this.getPixelComponent(pixel), state)).reverse();
		return <div>{list}</div>;
	}
}

// ONLY USE RENDERED PIXELS WHEN YOU NEED TO MONITOR
// A CHANGE IN THE STORE (THIS COULD RERENDER ANY SCRIPT TAGS AFTER LOAD)
export default @pure class RenderedPixels extends PixelRenderer {
	getPixelComponent(pixel) {
		return pixel.default || pixel.render;
	}
}

// USE THIS FOR BASIC SETUP STUFF
// IT WILL ONLY RUN ONCE AND THAT'S ON LOAD
// THIS WILL PREVENT TRACKING CODE (LIKE PAGE LOAD) BEING RUN MULTIPLE TIMES
@pure class StaticRenderedPixels extends PixelRenderer {
	getPixelComponent(pixel) {
		return pixel.staticRender;
	}
}

export { StaticRenderedPixels };
